import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { loginUser } from "../firebase/auth";

/* ─── Design tokens ───────────────────────────────────────── */
const T = {
  rose:      "#c0506e",
  roseDark:  "#8f2d46",
  roseLight: "#e8839a",
  roseSoft:  "#fdf0f3",
  roseMid:   "#f3c5cf",
  ivory:     "#faf8f5",
  ivoryDark: "#f0ece6",
  warm100:   "#e8e3dc",
  warm200:   "#d4cfc8",
  warm400:   "#9e9189",
  warm700:   "#3d3530",
  ink:       "#1a1510",
  ffDisplay: "'Cormorant Garamond', Georgia, serif",
  ffBody:    "'Outfit', system-ui, sans-serif",
};

/* ─── Eye icon ────────────────────────────────────────────── */
function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

/* ─── Field component ─────────────────────────────────────── */
function Field({ label, error, type = "text", rightElement, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: "1.1rem" }}>
      <label style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: error ? T.rose : focused ? T.rose : T.warm400,
        marginBottom: 6,
        fontFamily: T.ffBody,
        transition: "color 180ms",
      }}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={type}
          {...props}
          onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: rightElement ? "0.75rem 2.75rem 0.75rem 1rem" : "0.75rem 1rem",
            fontFamily: T.ffBody,
            fontSize: 14,
            color: T.ink,
            background: focused ? "#fff" : T.ivory,
            border: `1.5px solid ${error ? T.rose : focused ? T.rose : T.warm200}`,
            borderRadius: 10,
            outline: "none",
            transition: "border-color 200ms, background 200ms, box-shadow 200ms",
            boxShadow: focused ? `0 0 0 3px rgba(192,80,110,.12)` : "none",
          }}
        />
        {rightElement && (
          <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
            {rightElement}
          </div>
        )}
      </div>
      {error && (
        <p style={{ margin: "5px 0 0 2px", fontSize: 12, color: T.rose, fontFamily: T.ffBody }}>
          {error}
        </p>
      )}
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────── */
const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.auth);

  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched]           = useState({});
  const [error, setError]               = useState("");

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const errors = {
    email:    touched.email    && !isValidEmail(email) ? "Enter a valid email address" : null,
    password: touched.password && !password            ? "Password is required" : null,
  };

 const handleLogin = async (e) => {
  e.preventDefault();
  setTouched({ email: true, password: true });
  setError("");

  if (!isValidEmail(email) || !password) {
    setError("Please fill in all fields correctly.");
    return;
  }

  const result = await dispatch(loginUser(email, password)); // ✅ now returns { success, role/error }

  if (result.success) {
    // Redirect based on role
    if (result.role === "admin") {
      navigate("/admin");
    } else {
      navigate("/shop");
    }
  } else {
    const code = result.error || "";
    if (code.includes("user-not-found") || code.includes("wrong-password") || code.includes("invalid-credential")) {
      setError("Incorrect email or password. Please try again.");
    } else if (code.includes("too-many-requests")) {
      setError("Too many attempts. Please try again later.");
    } else {
      setError("Login failed. Please try again.");
    }
  }
};
  return (
    <>
      <Helmet>
        <title>Sign In | Glossy Lux Hair</title>
        <meta name="description" content="Log in to your Glossy Lux Hair account to shop premium wigs and hair extensions in Ghana." />
        <link rel="canonical" href="https://glossyluxhair.com/login" />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .login-card { animation: fadeUp 480ms cubic-bezier(0.16,1,0.3,1) both; }
        .login-submit:not(:disabled):hover {
          background: ${T.roseDark} !important;
          box-shadow: 0 8px 28px rgba(192,80,110,.32) !important;
          transform: translateY(-2px);
        }
        .login-submit { transition: background 220ms, box-shadow 220ms, transform 200ms; }
        .login-submit:disabled { opacity: .55; cursor: not-allowed; }
        .eye-btn:hover { color: ${T.rose} !important; }
        .eye-btn { transition: color 180ms; }
      `}</style>

      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(ellipse 70% 50% at 30% 20%, rgba(192,80,110,.07) 0%, transparent 60%),
          radial-gradient(ellipse 50% 40% at 80% 80%, rgba(192,80,110,.05) 0%, transparent 55%),
          ${T.ivoryDark}
        `,
        padding: "2rem 1rem",
        fontFamily: T.ffBody,
      }}>
        <div className="login-card" style={{
          width: "100%",
          maxWidth: 420,
          background: "#fff",
          borderRadius: 20,
          border: `1px solid ${T.warm100}`,
          boxShadow: `0 4px 24px rgba(26,21,16,.06), 0 20px 64px rgba(192,80,110,.08)`,
          overflow: "hidden",
        }}>

          {/* Accent bar */}
          <div style={{
            height: 3,
            background: `linear-gradient(90deg, transparent 0%, ${T.roseMid} 15%, ${T.rose} 50%, ${T.roseMid} 85%, transparent 100%)`,
          }} />

          <div style={{ padding: "clamp(1.75rem, 5vw, 2.5rem)" }}>

            {/* Header */}
            <div style={{ marginBottom: "2rem" }}>
              <Link to="/" style={{ textDecoration: "none" }}>
                <p style={{ margin: "0 0 1.25rem", fontFamily: T.ffDisplay, fontSize: "1.1rem", fontWeight: 700, color: T.rose, letterSpacing: "0.02em" }}>
                  GlossyLux<em style={{ fontStyle: "italic" }}>Hair</em>
                </p>
              </Link>
              <h1 style={{ margin: "0 0 .3rem", fontFamily: T.ffDisplay, fontSize: "clamp(1.7rem, 5vw, 2.1rem)", fontWeight: 700, color: T.ink, letterSpacing: "-0.02em" }}>
                Welcome back
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: T.warm400 }}>
                Sign in to continue shopping
              </p>
            </div>

            {/* Error alert */}
            {error && (
              <div style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "0.85rem 1rem",
                background: T.roseSoft,
                border: `1px solid ${T.roseMid}`,
                borderRadius: 10,
                marginBottom: "1.25rem",
              }}>
                <span style={{ color: T.rose, fontSize: 15, flexShrink: 0 }}>⚠</span>
                <p style={{ margin: 0, fontSize: 13, color: T.roseDark, fontFamily: T.ffBody }}>{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleLogin} noValidate>
              <Field
                label="Email Address"
                type="email"
                placeholder="you@example.com"
                value={email}
                error={errors.email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                autoComplete="email"
              />

              <Field
                label="Password"
                type={showPassword ? "text" : "password"}
                placeholder="Your password"
                value={password}
                error={errors.password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, password: true }))}
                autoComplete="current-password"
                rightElement={
                  <button
                    type="button"
                    className="eye-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    style={{ background: "none", border: "none", cursor: "pointer", color: T.warm400, padding: 0, display: "flex" }}
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                }
              />

              {/* Forgot password */}
              <div style={{ textAlign: "right", marginTop: "-0.5rem", marginBottom: "1.25rem" }}>
                <Link to="/forgot-password" style={{ fontSize: 12, color: T.rose, textDecoration: "none", fontWeight: 500 }}>
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="login-submit"
                style={{
                  width: "100%",
                  padding: "0.9rem",
                  fontFamily: T.ffBody,
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: "0.04em",
                  color: "#fff",
                  background: T.rose,
                  border: "none",
                  borderRadius: 12,
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: `0 4px 20px rgba(192,80,110,.22)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {loading ? (
                  <>
                    <span style={{
                      width: 16, height: 16,
                      border: "2px solid rgba(255,255,255,.4)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin .7s linear infinite",
                    }} />
                    Signing in…
                  </>
                ) : "Sign In"}
              </button>
            </form>

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "1.5rem 0" }}>
              <div style={{ flex: 1, height: 1, background: T.warm100 }} />
              <span style={{ fontSize: 12, color: T.warm400, fontWeight: 500 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: T.warm100 }} />
            </div>

            {/* Register link */}
            <p style={{ textAlign: "center", fontSize: 13, color: T.warm400, margin: 0 }}>
              Don't have an account?{" "}
              <Link to="/register" style={{ color: T.rose, fontWeight: 600, textDecoration: "none" }}>
                Create one free →
              </Link>
            </p>

          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;